import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createResource, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { subsidizersRepository } from '~/repositories/subsidizersRepository';
import type { Pagination } from '~/hooks';
import type { SubsidizersFilter } from '~/repositories/subsidizersRepository';
import type { CreateSubsidizerDto, SubsidizerPaginationDto, UpdateSubsidizerDto } from '~/swagger/portal/Api';

export const [SubsidizersProvider, useSubsidizers] = createMagicDoorContext('Subsidizers', () => {
  const [subsidizersData, setSubsidizersData] = createSignal<SubsidizerPaginationDto | undefined>(undefined);
  const [loading, setLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [currentFilter, setCurrentFilter] = createSignal<SubsidizersFilter | undefined>(undefined);
  const [pagination, setPagination] = createSignal<Pagination>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const getSubsidizers = async (filter?: SubsidizersFilter) => {
    setLoading(true);
    setCurrentFilter(filter);
    try {
      const response = await subsidizersRepository.getSubsidizers(filter);
      setSubsidizersData(response);
      setPagination({
        currentPage: response.currentPage,
        pageSize: response.pageSize,
        totalCount: response.totalCount,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSubsidizers = async () => {
    const filter = currentFilter();
    if (filter) {
      await getSubsidizers(filter);
    }
  };

  const [currentId, setCurrentId] = createSignal<string | undefined>(undefined);
  const [current, currentActions] = createResource(currentId, (id) => subsidizersRepository.getSubsidizer({ id }));

  const [batchSubsidizers, getBatchSubsidizers, batchActions] = createTriggerResource(() => subsidizersRepository.getSubsidizersBatch());

  const addSubsidizer = createMutation(async (model: CreateSubsidizerDto) => {
    const created = await subsidizersRepository.createSubsidizer(model);
    currentActions.mutate(created);
    await refreshSubsidizers();
    return created;
  });

  const updateSubsidizer = createMutation(async (subsidizerId: string, model: UpdateSubsidizerDto) => {
    const updated = await subsidizersRepository.updateSubsidizer({ id: subsidizerId }, model);
    if (subsidizerId === currentId()) currentActions.mutate(updated);
    await refreshSubsidizers();
    return updated;
  });

  const deleteSubsidizer = createMutation(async (subsidizerId: string) => {
    await subsidizersRepository.deleteSubsidizer({ id: subsidizerId });
    if (subsidizerId === currentId()) {
      setCurrentId(undefined);
      currentActions.mutate(undefined);
    }
    await refreshSubsidizers();
  });

  const deactivateSubsidizer = createMutation(async (subsidizerId: string) => {
    const updated = await subsidizersRepository.deactivateSubsidizer({ id: subsidizerId });
    if (subsidizerId === currentId()) currentActions.mutate(updated);
    await refreshSubsidizers();
    return updated;
  });

  const reactivateSubsidizer = createMutation(async (subsidizerId: string) => {
    const updated = await subsidizersRepository.reactivateSubsidizer({ id: subsidizerId });
    if (subsidizerId === currentId()) currentActions.mutate(updated);
    await refreshSubsidizers();
    return updated;
  });

  return {
    subsidizersData,
    loading,
    error,
    getSubsidizers,
    refreshSubsidizers,

    currentId,
    setCurrentId,
    current,
    currentActions,
    pagination,

    batchSubsidizers,
    getBatchSubsidizers,
    batchActions,

    addSubsidizer,
    updateSubsidizer,
    deleteSubsidizer,
    deactivateSubsidizer,
    reactivateSubsidizer,
  };
});
